import type { ChipProps } from '@mui/material';

import { SeedDataImportStatus } from '../../graphql/__generated__/graphql';

export const MAX_SEED_DATA_FILE_BYTES = 5 * 1024 * 1024 * 1024;

export const isActiveSeedDataImportStatus = (status: SeedDataImportStatus): boolean =>
  status === SeedDataImportStatus.AwaitingUpload ||
  status === SeedDataImportStatus.Queued ||
  status === SeedDataImportStatus.Processing;

export const isSeedDataFileValid = (file: File): boolean => {
  const isXlsx = file.name.toLocaleLowerCase('en-US').endsWith('.xlsx') && file.name.length > '.xlsx'.length;

  return isXlsx && Number.isFinite(file.size) && Number.isSafeInteger(file.size) && file.size > 0 && file.size < MAX_SEED_DATA_FILE_BYTES;
};

export const getSeedDataFileValidationMessage = (file: File | null): string | null => {
  if (file === null) return 'Choose an XLSX file before uploading.';
  if (!file.name.toLocaleLowerCase('en-US').endsWith('.xlsx') || file.name.length <= '.xlsx'.length) {
    return 'Choose an XLSX file.';
  }
  if (!Number.isFinite(file.size) || !Number.isSafeInteger(file.size) || file.size <= 0) {
    return 'Choose a non-empty XLSX file with a valid whole-byte size.';
  }
  if (file.size >= MAX_SEED_DATA_FILE_BYTES) {
    return 'Choose an XLSX file smaller than 5 GiB.';
  }

  return null;
};

export const formatSeedDataBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KiB', 'MiB', 'GiB'] as const;
  const unitIndex = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)) - 1);
  const value = bytes / 1024 ** (unitIndex + 1);
  return `${value.toLocaleString(undefined, { maximumFractionDigits: value >= 10 ? 0 : 1 })} ${units[unitIndex]}`;
};

export const formatSeedDataNumber = (value: number): string =>
  Number.isFinite(value) ? value.toLocaleString() : '—';

export const formatSeedDataTimestamp = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim().length === 0) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export type SeedDataStatusPresentation = {
  label: string;
  color: ChipProps['color'];
};

export const getSeedDataStatusPresentation = (status: SeedDataImportStatus): SeedDataStatusPresentation => {
  switch (status) {
    case SeedDataImportStatus.AwaitingUpload:
      return { label: 'Awaiting upload', color: 'warning' };
    case SeedDataImportStatus.Queued:
      return { label: 'Queued', color: 'info' };
    case SeedDataImportStatus.Processing:
      return { label: 'Processing', color: 'info' };
    case SeedDataImportStatus.Succeeded:
      return { label: 'Succeeded', color: 'success' };
    case SeedDataImportStatus.SucceededWithErrors:
      return { label: 'Succeeded with errors', color: 'warning' };
    case SeedDataImportStatus.Failed:
      return { label: 'Failed', color: 'error' };
    case SeedDataImportStatus.Cancelled:
      return { label: 'Cancelled', color: 'default' };
    case SeedDataImportStatus.Expired:
      return { label: 'Expired', color: 'default' };
  }
};
